import {
  BaseSyntheticEvent,
  FormEvent,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useForm } from 'react-hook-form'
import { motion, AnimatePresence } from 'motion/react'
import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile'
import cx from 'classnames'
import { SanityNewsletterBlock } from '@data/sanity/queries/types/blocks'
import { fadeAnimation } from '@lib/animate'
import { LanguageContext } from '@lib/language'
import { addEmailToNewsletterList } from '@lib/services'
import { StringsContext } from '@lib/strings'
import Alert from '@components/alert'
import Button, {
  ButtonColor,
  ButtonSize,
  ButtonVariant,
} from '@components/buttons/button'
import Checkbox from '@components/checkbox'
import InputField from '@components/input-field'
import SimplePortableText from '@components/portable-text/simple'
import ComplexPortableText from '@components/portable-text/complex'
import { getPageLocation } from '@lib/routes'
import { triggerGoogleTagManagerEvent } from '@lib/analytics/event'

interface NewsletterProps extends Omit<
  SanityNewsletterBlock,
  '_key' | '_type'
> {
  id: string
  className?: string
  isInvertedColors?: boolean
  isInlineButton?: boolean
}

interface NewsletterFormValues {
  fullname: string
  email: string
  acceptTerms?: boolean
}

const turnstileSiteKey = process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_SITE_KEY

const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i

const Newsletter = ({
  id,
  service,
  hubSpotFormId,
  klaviyoListID,
  variant,
  title,
  description,
  terms,
  submit,
  successMsg,
  errorMsg,
  className,
  isInvertedColors,
  isInlineButton,
}: NewsletterProps) => {
  const { locale } = useContext(LanguageContext)
  const strings = useContext(StringsContext)

  const turnstileRef = useRef<TurnstileInstance>(null)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [isError, setIsError] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)

  const { handleSubmit, register, watch, reset } =
    useForm<NewsletterFormValues>()

  // Wait for successful submit to send event
  useEffect(() => {
    if (isSuccess) {
      triggerGoogleTagManagerEvent('newsletter_signup', {
        page_title: document.title,
        page_location: getPageLocation(),
      })
    }
  }, [isSuccess])

  const hasAcceptedTerms = terms ? watch('acceptTerms') : true
  const email = watch('email')

  // Call to reset the form
  const resetForm = (e: FormEvent) => {
    e.preventDefault()
    reset()
    setIsError(false)
    setIsSuccess(false)
    setIsSubmitting(false)
    setTurnstileToken(null)
    turnstileRef.current?.reset()
  }

  // handle form submission
  const onSubmit = async (
    newsletterFormValues: NewsletterFormValues,
    event?: BaseSyntheticEvent,
  ) => {
    event?.preventDefault()

    setIsError(false)
    setIsSuccess(false)

    const newsletterTargetId =
      service === 'klaviyo'
        ? klaviyoListID
        : service === 'hubSpot'
          ? hubSpotFormId
          : null

    // Show an error if there is no newsletter target
    if (!newsletterTargetId) {
      setIsError(true)
      return
    }

    // Stop if accepting of terms is required
    if (!hasAcceptedTerms && terms) {
      return
    }

    if (!turnstileToken) {
      setIsError(true)
      return
    }

    setIsSubmitting(true)

    try {
      await addEmailToNewsletterList(
        locale,
        service,
        newsletterTargetId,
        !!hasAcceptedTerms,
        newsletterFormValues.email,
        turnstileToken,
        newsletterFormValues.fullname,
      )
      setIsSuccess(true)
    } catch (error) {
      console.log(error)
      setIsError(true)
    }

    setTurnstileToken(null)
    turnstileRef.current?.reset()

    setIsSubmitting(false)
  }

  const fullnameRegister = register('fullname')
  const emailRegister = register('email', {
    required: strings.emailMissing,
    pattern: {
      value: emailRegex,
      message: strings.emailInvalid,
    },
  })
  const acceptTermsRegister = register('acceptTerms')

  const isExtended = variant === 'extended'
  const isDisabled =
    isSubmitting ||
    !hasAcceptedTerms ||
    !emailRegex.test(email) ||
    !turnstileToken

  if (!service) {
    return null
  }

  const form = (
    <form className={cx(className)} onSubmit={handleSubmit(onSubmit)}>
      <AnimatePresence mode="wait">
        {!isError && !isSuccess && (
          <motion.div
            initial="hide"
            animate="show"
            exit="hide"
            variants={fadeAnimation}
          >
            <input
              type="text"
              autoComplete="off"
              className="hidden"
              aria-hidden="true"
              onChange={fullnameRegister.onChange}
              onBlur={fullnameRegister.onBlur}
              ref={fullnameRegister.ref}
              name={fullnameRegister.name}
            />

            {isExtended ? (
              <input
                id={`newsletter-${id}-email`}
                type="email"
                onChange={emailRegister.onChange}
                onBlur={emailRegister.onBlur}
                ref={emailRegister.ref}
                name={emailRegister.name}
                placeholder={strings.emailAddress}
                className={cx(
                  'p-4 mb-5 rounded-full w-full max-w-[300px] sm:max-w-[400px] text-black',
                )}
              />
            ) : (
              <InputField
                id={`newsletter-${id}-email`}
                type="email"
                formRegister={emailRegister}
                placeholder={strings.emailAddress}
                borderBottom
                className="mb-4"
                isInvertedColors={isInvertedColors}
              >
                {isInlineButton && (
                  <Button
                    id={`newsletter-${id}-submit`}
                    className="ml-3 text-xs whitespace-nowrap uppercase"
                    type="submit"
                    size={ButtonSize.SMALL}
                    disabled={isDisabled}
                  >
                    {submit}
                  </Button>
                )}
              </InputField>
            )}

            {terms && (
              <Checkbox
                id={`newsletter-${id}-acceptTerms`}
                formRegister={acceptTermsRegister}
                className="mb-4"
                isInvertedColors={isInvertedColors}
              >
                <SimplePortableText
                  content={terms}
                  className="rc rc-checkbox"
                />
              </Checkbox>
            )}

            {!!turnstileSiteKey && (
              <div className="mt-2 mb-3 flex justify-center">
                <Turnstile
                  ref={turnstileRef}
                  siteKey={turnstileSiteKey}
                  options={{
                    theme: 'auto',
                    appearance: 'interaction-only',
                  }}
                  onSuccess={setTurnstileToken}
                  onError={() => {
                    setTurnstileToken(null)
                  }}
                  onExpire={() => {
                    setTurnstileToken(null)
                  }}
                />
              </div>
            )}

            {!isInlineButton && (
              <Button
                id={`newsletter-${id}-submit`}
                type="submit"
                className={cx({
                  'w-full mt-3': !isExtended,
                  'w-auto uppercase mt-1': isExtended,
                })}
                size={ButtonSize.NORMAL}
                color={ButtonColor.GREEN_LIGHT}
                variant={ButtonVariant.FILLED}
                disabled={isDisabled}
              >
                {submit}
              </Button>
            )}
          </motion.div>
        )}

        {isSuccess && (
          <motion.div
            key="success"
            initial="hide"
            animate="show"
            exit="hide"
            variants={fadeAnimation}
            className={cx({
              'max-w-[400px] mx-auto': isExtended,
            })}
          >
            <Alert>
              <ComplexPortableText
                content={successMsg}
                className="rc rc-alert"
              />
            </Alert>
          </motion.div>
        )}

        {isError && (
          <motion.div
            key="error"
            initial="hide"
            animate="show"
            exit="hide"
            variants={fadeAnimation}
            className={cx({
              'max-w-[400px] mx-auto': isExtended,
            })}
          >
            <Alert
              buttonText={strings.buttonTryAgain}
              onClick={resetForm}
              buttonColor={
                isExtended ? ButtonColor.GREEN_LIGHT : ButtonColor.DEFAULT
              }
            >
              <ComplexPortableText content={errorMsg} className="rc rc-alert" />
            </Alert>
          </motion.div>
        )}
      </AnimatePresence>
    </form>
  )

  if (isExtended) {
    return (
      <div className="bg-green-light text-black py-8 xs:py-10 md:py-12 lg:py-20 px-5 xs:px-10 flex flex-col justify-center items-center rounded-md">
        {title && <h4 className="text-center mb-4 max-w-4xl">{title}</h4>}

        {description && (
          <p className="text-base text-center max-w-3xl">{description}</p>
        )}

        <div className="w-full text-center mt-8">{form}</div>
      </div>
    )
  }

  return form
}

export default Newsletter
